import React, { useEffect, useState } from 'react';
import { AppLayout } from '@/components/AppLayout';
import { LayoutDashboard, Users, Building2, Wallet, UserCog, Plus, MoreVertical, ShieldOff, ShieldCheck, Trash2, User, ClipboardCheck, KeyRound, ScrollText, Upload, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';

function FilePicker({ label, testid, value, onChange }) {
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { alert('Fichier trop lourd (max 5 Mo)'); return; }
    const r = new FileReader();
    r.onload = () => onChange(r.result);
    r.readAsDataURL(f);
  };
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5">{label}</label>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 h-10 px-3 rounded-md border border-dashed border-slate-300 text-sm cursor-pointer hover:bg-slate-50">
          <Upload className="w-4 h-4 text-[#1677FF]" />
          <span>{value ? 'Remplacer' : 'Choisir (max 5 Mo)'}</span>
          <input data-testid={testid} type="file" accept="image/*" onChange={onFile} className="hidden" />
        </label>
        {value && <img src={value} alt="preview" className="w-14 h-14 rounded object-cover border" />}
      </div>
    </div>
  );
}

import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { TID } from '@/constants/testIds';
import { Wizard } from '@/components/Wizard';
import { Loader } from '@/components/Loader';
import { useTranslation } from 'react-i18next';

const links = [
  { to: '/admin', end: true, label: 'Tableau de bord', icon: LayoutDashboard },
  { to: '/admin/bailleurs', label: 'Bailleurs', icon: Building2 },
  { to: '/admin/locataires', label: 'Locataires', icon: Users },
  { to: '/admin/agents', label: 'Agents', icon: UserCog },
  { to: '/admin/abonnements', label: 'Abonnements', icon: Wallet },
  { to: '/admin/catalogue', label: 'Paramètres', icon: UserCog },
  { to: '/admin/audit', label: 'Audit', icon: ScrollText },
];

function CreateAgentDialog({ onCreated }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const initialForm = {
    prenom: '', nom: '', email: '', telephone: '',
    poste: '', sexe: '', date_naissance: '',
    type_piece: '', numero_piece: '',
    photo_piece_recto: null, photo_piece_verso: null, photo_agent: null,
  };
  const [creds, setCreds] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (form) => {
    setLoading(true);
    try {
      const { data } = await adminApi.createAgent(form);
      setCreds(data.credentials);
      toast.success('Agent créé');
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
      throw err;
    } finally { setLoading(false); }
  };
  const close = () => { setOpen(false); setCreds(null); };

  const steps = [
    {
      key: 'identity',
      title: t('wizard.agent.step1'),
      icon: User,
      validate: (f) => {
        const e = [];
        if (!f.prenom?.trim()) e.push('Prénom requis');
        if (!f.nom?.trim()) e.push('Nom requis');
        if (!f.email?.trim() || !/.+@.+\..+/.test(f.email)) e.push('Email valide requis');
        if (!f.telephone?.trim()) e.push('Téléphone requis');
        return e;
      },
      render: (f, set) => (
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Prénom *</Label><Input data-testid={TID.caPrenom} value={f.prenom} onChange={(e) => set('prenom', e.target.value)} className="mt-1.5" /></div>
          <div><Label>Nom *</Label><Input data-testid={TID.caNom} value={f.nom} onChange={(e) => set('nom', e.target.value)} className="mt-1.5" /></div>
          <div><Label>Email *</Label><Input data-testid={TID.caEmail} type="email" value={f.email} onChange={(e) => set('email', e.target.value)} className="mt-1.5" /></div>
          <div><Label>Téléphone *</Label><Input data-testid={TID.caTelephone} value={f.telephone} onChange={(e) => set('telephone', e.target.value)} placeholder="+243..." className="mt-1.5" /></div>
        </div>
      ),
    },
    {
      key: 'profile',
      title: 'Profil professionnel',
      icon: User,
      render: (f, set) => (
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><Label>Poste *</Label><Input data-testid="ca-poste" value={f.poste} onChange={(e) => set('poste', e.target.value)} placeholder="Agent terrain, Responsable secteur..." className="mt-1.5" /></div>
          <div>
            <Label>Sexe *</Label>
            <select data-testid="ca-sexe" value={f.sexe} onChange={(e) => set('sexe', e.target.value)} className="mt-1.5 h-10 w-full rounded-md border border-slate-200 px-3 text-sm">
              <option value="">— Choisir —</option>
              <option value="M">Masculin</option>
              <option value="F">Féminin</option>
            </select>
          </div>
          <div><Label>Date de naissance *</Label><Input data-testid="ca-dob" type="date" value={f.date_naissance} onChange={(e) => set('date_naissance', e.target.value)} className="mt-1.5" /></div>
        </div>
      ),
      validate: (f) => {
        const e = [];
        if (!f.poste?.trim()) e.push('Poste requis');
        if (!f.sexe) e.push('Sexe requis');
        if (!f.date_naissance) e.push('Date de naissance requise');
        return e;
      },
    },
    {
      key: 'idcard',
      title: 'Pièce d\'identité',
      icon: ClipboardCheck,
      render: (f, set) => (
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Type de pièce *</Label>
              <select data-testid="ca-typepiece" value={f.type_piece} onChange={(e) => set('type_piece', e.target.value)} className="mt-1.5 h-10 w-full rounded-md border border-slate-200 px-3 text-sm">
                <option value="">— Choisir —</option>
                <option value="CNI">CNI</option>
                <option value="Passeport">Passeport</option>
                <option value="Carte consulaire">Carte consulaire</option>
                <option value="Permis de conduire">Permis de conduire</option>
              </select>
            </div>
            <div><Label>Numéro *</Label><Input data-testid="ca-numpiece" value={f.numero_piece} onChange={(e) => set('numero_piece', e.target.value)} className="mt-1.5" /></div>
          </div>
          <FilePicker label="Photo recto *" testid="ca-recto" value={f.photo_piece_recto} onChange={(b64) => set('photo_piece_recto', b64)} />
          <FilePicker label="Photo verso *" testid="ca-verso" value={f.photo_piece_verso} onChange={(b64) => set('photo_piece_verso', b64)} />
          <FilePicker label="Photo de l'agent (portrait) *" testid="ca-portrait" value={f.photo_agent} onChange={(b64) => set('photo_agent', b64)} />
        </div>
      ),
      validate: (f) => {
        const e = [];
        if (!f.type_piece) e.push('Type de pièce requis');
        if (!f.numero_piece?.trim()) e.push('Numéro de pièce requis');
        if (!f.photo_piece_recto) e.push('Photo recto requise');
        if (!f.photo_piece_verso) e.push('Photo verso requise');
        if (!f.photo_agent) e.push('Photo de l\'agent requise');
        return e;
      },
    },
    {
      key: 'confirm',
      title: t('wizard.agent.step2'),
      icon: ClipboardCheck,
      render: (f) => (
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-5 space-y-3 text-sm">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Récapitulatif</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><div className="text-xs text-slate-500">Nom complet</div><div className="font-semibold text-[#0D1B2A]">{f.prenom} {f.nom}</div></div>
            <div><div className="text-xs text-slate-500">Poste</div><div className="font-semibold text-[#0D1B2A]">{f.poste}</div></div>
            <div><div className="text-xs text-slate-500">Email</div><div className="font-semibold text-[#0D1B2A]">{f.email}</div></div>
            <div><div className="text-xs text-slate-500">Téléphone</div><div className="font-semibold text-[#0D1B2A]">{f.telephone}</div></div>
            <div><div className="text-xs text-slate-500">Sexe</div><div className="font-semibold text-[#0D1B2A]">{f.sexe === 'M' ? 'Masculin' : 'Féminin'}</div></div>
            <div><div className="text-xs text-slate-500">Né(e) le</div><div className="font-semibold text-[#0D1B2A]">{f.date_naissance}</div></div>
            <div className="sm:col-span-2"><div className="text-xs text-slate-500">Pièce</div><div className="font-semibold text-[#0D1B2A]">{f.type_piece} · {f.numero_piece}</div></div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => v ? setOpen(true) : close()}>
      <DialogTrigger asChild>
        <Button data-testid={TID.adminCreateAgent} className="bg-[#1677FF] hover:bg-[#0E62DB]">
          <Plus className="w-4 h-4 mr-1.5" /> Nouvel agent
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserCog className="w-5 h-5 text-[#1677FF]" /> {t('wizard.agent.title')}</DialogTitle>
          <DialogDescription>L'agent pourra créer et suivre ses propres bailleurs.</DialogDescription>
        </DialogHeader>
        {!creds ? (
          <Wizard
            steps={steps}
            initialForm={initialForm}
            onSubmit={submit}
            submitLabel={t('wizard.agent.submit')}
            loading={loading}
            testidPrefix="ca-wizard"
          />
        ) : (
          <div data-testid={TID.caCredentials} className="space-y-4">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-emerald-900">Agent créé</div>
                <div className="mt-1 text-xs text-emerald-800">Transmettez-lui ces identifiants ; il changera son mot de passe à la première connexion.</div>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 p-4 bg-[#0D1B2A] text-white">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/60"><User className="w-3 h-3" /> Identifiant</div>
              <div className="text-lg font-bold">{creds.identifier}</div>
              <div className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/60"><KeyRound className="w-3 h-3" /> Mot de passe temporaire</div>
              <div className="text-lg font-bold font-mono">{creds.temp_password}</div>
            </div>
            <DialogFooter><Button onClick={close} className="bg-[#1677FF] hover:bg-[#0E62DB]">Terminer</Button></DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminAgents() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const { data } = await adminApi.listAgents(); setItems(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (a) => {
    await adminApi.toggleAgent(a.id, !a.is_active);
    toast.success(!a.is_active ? 'Agent activé' : 'Agent désactivé');
    load();
  };
  const remove = async (a) => {
    if (!window.confirm(`Supprimer l'agent ${a.full_name} ?`)) return;
    await adminApi.deleteAgent(a.id);
    toast.success('Agent supprimé');
    load();
  };

  return (
    <AppLayout links={links} testid={TID.adminSidebar} title="Agents">
      <div className="flex items-center justify-between mb-5">
        <div className="text-sm text-slate-500">Chaque agent gère un portefeuille de bailleurs qu'il a créés.</div>
        <CreateAgentDialog onCreated={load} />
      </div>

      <div data-testid={TID.adminAgentsTable} className="lb-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                {['Agent', 'Contact', 'Portefeuille', 'Statut', ''].map((h) => (
                  <th key={h} className="px-4 py-3 text-[11px] font-semibold uppercase tracking-widest text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="px-4 py-8"><Loader variant="full" /></td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-12 text-center">
                  <div className="text-slate-500">Aucun agent.</div>
                  <div className="text-xs text-slate-400 mt-1">Créez votre premier agent pour lui déléguer la gestion.</div>
                </td></tr>
              )}
              {items.map((a) => (
                <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#0D1B2A]">{a.full_name}</div>
                    <div className="text-xs text-slate-500">Créé le {new Date(a.created_at).toLocaleDateString('fr-FR')}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-slate-700">{a.email}</div>
                    <div className="text-xs text-slate-500">{a.telephone || '—'}</div>
                  </td>
                  <td className="px-4 py-3 font-semibold text-[#0D1B2A]">{a.portfolio_count} bailleur(s)</td>
                  <td className="px-4 py-3">
                    {a.is_active ? (
                      <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50">Actif</Badge>
                    ) : (
                      <Badge className="bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100">Inactif</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Link to={`/admin/agents/${a.id}`} data-testid={`agent-view-${a.id}`}>
                        <Button variant="ghost" size="icon" title="Voir la fiche"><Eye className="w-4 h-4" /></Button>
                      </Link>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => toggle(a)}>
                            {a.is_active ? (<><ShieldOff className="w-4 h-4 mr-2" />Désactiver</>) : (<><ShieldCheck className="w-4 h-4 mr-2" />Activer</>)}
                          </DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => remove(a)} className="text-red-600 focus:text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AppLayout>
  );
}
